import { EvTestCarDataType } from '@apps/components/EvTest/EvTestCarCard/props';
import { EvTestSectionBox } from '@apps/components/EvTest/EvTestSectionBox';
import { Box, Stack } from '@mui/material';
import EvTestEvaluationImage from '@stack/assets/images/ev-evaluation-system.svg';
import evTestDesktopBackgroundImage from '@stack/assets/images/ev-test-desktop.png';
import evTestMobileBackgroundImage from '@stack/assets/images/ev-test-mobile.png';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';

type EvTestScreenProps = {
  top_range_performance: EvTestCarDataType[];
  top_fast_charging_times: EvTestCarDataType[];
  top_power_performance: EvTestCarDataType[];
  webView?: boolean;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const EvTestScreen: React.FC<EvTestScreenProps> = (props) => {
  const {
    top_range_performance,
    top_fast_charging_times,
    top_power_performance,
    webView = false,
  } = props;

  const { isWeb } = useGetResponsive();
  const boxPadding = isWeb ? '30px' : '18px';
  const boxBlur = '6px';

  const backgroundImageSrc = isWeb
    ? evTestDesktopBackgroundImage.src
    : evTestMobileBackgroundImage.src;

  // =============== BREADCRUMB PATH
  const { basePath } = createBasePath({ webView, type: 'cars' });
  const breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: 'AutoFacts',
      url: basePath,
    },
    {
      title: 'EV Test',
      url: '',
    },
  ];

  const renderEvEvaluationSystemBox = () => {
    return (
      <Stack
        direction={{
          lg: 'row',
          xs: 'column',
        }}
        sx={{
          maxWidth: {
            lg: '50%',
            xs: '100%',
          },
        }}
        style={{
          alignItems: 'center',
          gap: 20,
          padding: boxPadding,
          backgroundColor: '#FFFFFF99',
          border: '2px solid #fff',
          borderRadius: '8px',
          backdropFilter: `blur(${boxBlur})`,
          WebkitBackdropFilter: `blur(${boxBlur})`,
        }}
      >
        <Stack
          style={{
            gap: isWeb ? 15 : 10,
          }}
        >
          <h1
            style={{
              fontSize: isWeb ? 24 : 16,
              fontWeight: 700,
              margin: 0,
            }}
          >
            EV Evaluation System
          </h1>
          <p
            style={{
              fontSize: isWeb ? 16 : 14,
              fontWeight: 400,
            }}
          >
            Caricarz evaluates new energy vehicles across six key dimensions:
            range, charging, power, safety, technology, and comfort.
            {isWeb ? (
              <br
                style={{
                  marginTop: 10,
                }}
              />
            ) : (
              ' '
            )}
            By assessing these dimensions, Caricarz provides a one-stop shopping
            experience for new energy cons.
          </p>
        </Stack>
        <Box>
          <EvTestEvaluationImage />
        </Box>
      </Stack>
    );
  };

  return (
    <div
      style={{
        backgroundImage: `url(${backgroundImageSrc}), linear-gradient(to bottom, rgba(226, 238, 241, 1) 95%, rgba(255,255,255,1) 100%)`,
        backgroundSize: '100vw auto, cover',
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundPosition: isWeb
          ? 'center calc(-63vw + 30%), center top'
          : 'center top, center top',
      }}
    >
      <div className="body-wrapper">
        {/* BREADCRUMB */}
        {!webView && (
          <Stack
            sx={{
              display: 'flex',
            }}
          >
            <Breadcrumb options={breadcrumbOpt} bold />
          </Stack>
        )}

        {/* CONTENT */}
        <Stack
          style={{
            gap: isWeb ? 40 : 15,
            marginTop: isWeb ? 80 : '30vh',
          }}
        >
          {renderEvEvaluationSystemBox()}

          <EvTestSectionBox
            sectionType="top_range_performance"
            sectionData={top_range_performance || []}
            containerProps={{
              padding: boxPadding,
              backdropFilter: `blur(${boxBlur})`,
              WebkitBackdropFilter: `blur(${boxBlur})`,
            }}
            webView={webView}
          />
          <EvTestSectionBox
            sectionType="top_fast_charging_times"
            sectionData={top_fast_charging_times || []}
            containerProps={{
              padding: boxPadding,
              backdropFilter: `blur(${boxBlur})`,
              WebkitBackdropFilter: `blur(${boxBlur})`,
            }}
            webView={webView}
          />
          <EvTestSectionBox
            sectionType="top_power_performance"
            sectionData={top_power_performance || []}
            containerProps={{
              padding: boxPadding,
              backdropFilter: `blur(${boxBlur})`,
              WebkitBackdropFilter: `blur(${boxBlur})`,
            }}
            webView={webView}
          />
        </Stack>
      </div>
    </div>
  );
};
